/**
 * Maps a raw Resales-Online API property item (and agency) into the view-model
 * the prototype's components consume. Field names mirror the production app
 * (see ListProperties / DetailBuilt / AccordionFER / AccordionFeatures).
 *
 * Reuses the app's own `format` + `converters` helpers so currency / unit output
 * is identical to the live property-viewer.
 */
import format from '../../utils/format';
import Converter from '../../constants/converters';
import { asset } from './assets';

const FALLBACK_IMG = asset('prototype/villa-1.svg');
const FALLBACK_LOGO = asset('prototype/agency-logo.svg');

// Media-v3 CDN URLs are the only ones that serve a "/thumbnails/" size; on the
// older media CDN the 400-wide equivalent is "/w400/" (v3 has no w400 at all).
//
// v3 photo URLs are built as {cdn}/{agencyId}/properties/{rsId}/{size}/{file}
// (buildPropertyPhotoV3Url in propq-rp-property) and the host differs per
// environment — media-service-cdn-dev.resales-online.com, media-rsov3…, etc. —
// so match the path shape as well as the known host names rather than relying on
// a single hostname.
const isMediaV3 = (url) => /media-service-cdn|media-v3|rsov3/i.test(url)
    || /\/properties\/\d+\//.test(url);

// The feed hands back media over plain http://, but the viewer is served over
// https — so the browser treats every photo as mixed content and either blocks it
// outright or silently auto-upgrades it (behaviour varies by browser). The media
// CDN serves the same objects over https, so upgrade the scheme explicitly and
// leave nothing to chance.
const secureUrl = (url) => (typeof url === 'string' ? url.replace(/^http:\/\//i, 'https://') : url);

// Feed photo URLs carry a CDN size segment — "/thumbnails/" or "/w<NNN>/". Swap it
// for the requested size ('thumbnails' | 'w800' | 'w1200' | …) so each context
// loads an appropriately sized image. Asking for 'thumbnails' on a non-media-v3
// URL falls back to "/w400/". Non-matching URLs (e.g. the local SVG fallback) are
// returned unchanged.
// The size variants to advertise in a srcset, paired with the pixel width each
// one represents. The 400-wide variant is named differently per CDN: media-v3
// serves it as the "thumbnails" segment, the older CDN as "w400" (see
// MediaService::parseImageFormat in resales-online-application). w1600 is
// deliberately absent — it returns empty images under the v3 rules, so 1200 is
// the effective maximum.
const V3_VARIANTS = [
    { size: 'thumbnails', width: 400 }, // v3's 400-wide variant
    { size: 'w800', width: 800 },
    { size: 'w1200', width: 1200 },
];
const LEGACY_VARIANTS = [
    { size: 'w400', width: 400 },
    { size: 'w800', width: 800 },
    { size: 'w1200', width: 1200 },
];
export const cdnVariants = (url) => (isMediaV3(url) ? V3_VARIANTS : LEGACY_VARIANTS);

export function cdnSize(url, size) {
    if (typeof url !== 'string') return url;
    const target = size === 'thumbnails' && !isMediaV3(url) ? 'w400' : size;
    return url.replace(/\/(?:thumbnails|w\d+)\//, `/${target}/`);
}

// Removes HTML tags from feed text — some descriptions arrive with tags (<p>,
// <h1>, <b>, <br>, …). Only the tags are stripped; the text between them,
// including its line breaks, is left exactly as-is (the description renders with
// white-space: pre-line, so real newlines are preserved).
export function stripHtml(html) {
    return typeof html === 'string' ? html.replace(/<[^>]*>/g, '') : '';
}

const num = (v) => (typeof v === 'number' ? v : parseFloat(v)) || 0;

// --- Buyer purchase-cost tax config (ESTIMATE ONLY — pending legal review) -----
// Second-hand transfer tax (ITP) general rate and stamp duty (AJD) rate, per
// autonomous community. These rates are legally sensitive, change over time, and
// MUST be confirmed and maintained by legal (see the buying-costs strategy doc).
// Only the two live markets are mapped today; extend as regions are added.
const ITP_BY_COMMUNITY = { valencia: 9, andalucia: 7 };
// Comunitat Valenciana (Ley 5/2025, from 1 June 2026): 9% generally, but 11%
// once the value EXCEEDS €1,000,000 — applied to the WHOLE value, not just the
// excess (a cliff, not a bracket). The threshold is in euros, so it's only
// applied to EUR prices; other currencies keep the general rate.
const ITP_HIGH_VALUE = { valencia: { threshold: 1000000, pct: 11 } };
function itpRate(community, price, currency) {
    const high = ITP_HIGH_VALUE[community];
    const isEur = !currency || String(currency).toUpperCase() === 'EUR';
    if (high && isEur && price > high.threshold) return high.pct;
    return ITP_BY_COMMUNITY[community];
}
const AJD_BY_COMMUNITY = { valencia: 1.5, andalucia: 1.2 };
const COMMUNITY_BY_PROVINCE = {
    // Comunidad Valenciana
    alicante: 'valencia', alacant: 'valencia', valencia: 'valencia', castellon: 'valencia', castello: 'valencia',
    // Andalucía
    almeria: 'andalucia', cadiz: 'andalucia', cordoba: 'andalucia', granada: 'andalucia',
    huelva: 'andalucia', jaen: 'andalucia', malaga: 'andalucia', sevilla: 'andalucia',
};
// Map the feed's province to its community. Unmapped provinces default to
// Valencia (the primary market) — flagged for review in the strategy doc.
function provinceToCommunity(province) {
    const p = String(province || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    return COMMUNITY_BY_PROVINCE[p] || 'valencia';
}

function money(value, currency) {
    if (value === undefined || value === null || value === '') return null;
    try {
        return format.formatCurrency(value, currency);
    } catch (e) {
        return null;
    }
}

function searchTypeLabel(t) {
    switch (t) {
        case 'LongTermRental': return 'for long term rental';
        case 'ShortTermRental': return 'for short term rental';
        default: return 'for sale';
    }
}

export function mapAgency(agency) {
    if (!agency || typeof agency !== 'object') return null;
    const logo = typeof agency.logo === 'string' && agency.logo
        // larger variant, per AccordionAgency; https so it isn't mixed content
        ? secureUrl(agency.logo.replace('agencies/1/1', 'agencies/1/2'))
        : FALLBACK_LOGO;
    return {
        name: agency.name || 'Agency',
        email: agency.email || '',
        phone: agency.phone || '',
        website: agency.website || '',
        contactSecureId: agency.contactSecureId != null ? agency.contactSecureId : null,
        // Carried for the gateway audit payload (AccountType / NewDevAccess).
        accountType: agency.accountType,
        newDevAccess: agency.newDevAccess,
        logo,
    };
}

// Per-agency property-viewer settings (from tblWhatsAppSettings). The backend
// already resolves them with defaults; this coerces defensively so a missing /
// old-shape payload still yields sane defaults (all sections shown, approx
// location). Contact email/phone are resolved server-side, so they're not here.
const DEFAULT_SETTINGS = {
    layout: 'default',
    offersLayout: 'list',      // 'list' | 'cards'      — desktop only (mobile always 'list')
    sectionFormat: 'standard', // 'standard' | 'compact'— desktop only (mobile always 'standard')
    location: 'approx',
    sortOrder: 'default',   // 'default' (as selected when shared) | 'price-asc' | 'price-desc'
    showUnits: true,
    showMap: true,
    showFeatures: true,
    social: {},
};

// Decode the backend `layout` token string into the two desktop layout axes.
// Tokens are hyphen-joined so a single WasLayout column can carry both
// (e.g. "cards-compact"); unknown/absent → list + standard (the current look).
function layoutAxes(layout) {
    const tokens = String(layout || '').toLowerCase().split('-');
    return {
        offersLayout: tokens.indexOf('cards') !== -1 ? 'cards' : 'list',
        sectionFormat: tokens.indexOf('compact') !== -1 ? 'compact' : 'standard',
    };
}

// TEMPORARY local-review override: force the layout axes from URL params
// (?offers=list|cards & ?section=standard|compact) so the designs can be
// reviewed before the admin tab / API gateway drives WasLayout. Unknown values
// are ignored. Remove this once the layout is wired to the backend.
export function overrideLayout(settings, params) {
    if (!params) return settings;
    const next = { ...settings };
    if (params.offers === 'list' || params.offers === 'cards') next.offersLayout = params.offers;
    if (params.section === 'standard' || params.section === 'compact') next.sectionFormat = params.section;
    return next;
}

export function mapSettings(raw) {
    if (!raw || typeof raw !== 'object') return { ...DEFAULT_SETTINGS };
    const social = raw.social && typeof raw.social === 'object' ? raw.social : {};
    const layout = raw.layout || 'default';
    const axes = layoutAxes(layout);
    return {
        layout,
        offersLayout: axes.offersLayout,
        sectionFormat: axes.sectionFormat,
        location: raw.location === 'exact' ? 'exact' : 'approx',
        sortOrder: raw.sortOrder === 'price-asc' || raw.sortOrder === 'price-desc' ? raw.sortOrder : 'default',
        // default to shown unless explicitly false
        showUnits: raw.showUnits !== false,
        showMap: raw.showMap !== false,
        showFeatures: raw.showFeatures !== false,
        // Keep only handles with an absolute http(s) scheme. The stored value is
        // untrusted (a forged admin POST can bypass the backoffice validation),
        // and a scheme-less or javascript: value would be hazardous as a raw
        // href on this public page — dropping it here covers every render site.
        social: ['facebook', 'instagram', 'linkedin', 'x'].reduce((acc, k) => {
            if (/^https?:\/\//i.test(social[k] || '')) acc[k] = social[k];
            return acc;
        }, {}),
    };
}

// Abbreviates a currency amount to k/m (e.g. 1,250,000 → "1.25m €") so a price
// RANGE doesn't overflow. Keeps the app's currency placement (EUR suffix, else
// prefix) and up to 2 decimals with trailing zeros stripped. Values under 1,000
// are left as a plain rounded number. The lowercase k/m suffix is rendered small
// by the Money component.
function abbrCurrency(value, currency) {
    const n = num(value);
    const sym = Converter.CURRENCY[currency] || '';
    const trim = (x) => String(parseFloat(x.toFixed(2)));
    let s;
    if (n >= 1e6) s = `${trim(n / 1e6)}m`;
    else if (n >= 1e3) s = `${trim(n / 1e3)}k`;
    else s = String(Math.round(n));
    return currency === 'EUR' ? `${s} ${sym}` : `${sym} ${s}`;
}

// Builds a price display string, appending " - <to>" when the property is a
// range (developments). Mirrors DetailHeader.js (`Price < PriceTo`). Ranges use
// abbreviated (K/M) amounts so "from - to" stays compact; single prices show in
// full.
function priceRange(item, currency) {
    const from = money(item.Price, currency);
    if (!from) return '—';
    if (num(item.Price) < num(item.PriceTo)) {
        return `${abbrCurrency(item.Price, currency)} - ${abbrCurrency(item.PriceTo, currency)}`;
    }
    return from;
}

// A {from, to} pair for an area/count stat. `to` is 0 unless it's a genuine
// upper bound (> from) — the view layer only shows the range when to > from.
function range(fromVal, toVal) {
    const from = num(fromVal);
    const to = num(toVal);
    return { from: Math.round(from), to: to > from ? Math.round(to) : 0 };
}

export function mapApiProperty(item, agency, index) {
    const unit = Converter.DIMENSION[item.Unit] || 'm²';
    const currency = item.Currency;

    // A selected development unit shared as its own offer (unitId set). It keeps
    // the parent's RsId, photos and description but carries the unit's exact
    // values: no ranges, no price list, no development-level fallbacks.
    const isUnitOffer = item.unitId != null;
    const isUnavailable = isUnitOffer && item.availability === 'unavailable';

    const intFloor = (num(item.UsefulArea) > 0 || isUnitOffer) ? num(item.UsefulArea) : num(item.UsefulAreaAuto);
    const isDevelopment = !!item.IsDevelopment;

    const beds = parseInt(item.RsBeds, 10) || 0;
    const bedsTo = parseInt(item.RsBedsTo, 10) || 0;
    const baths = parseFloat(item.RsBaths) || 0;
    const bathsTo = parseFloat(item.RsBathsTo) || 0;

    // features — keep the API's category grouping for the "View all" dialog,
    // and a flattened list for the inline preview / count. Categories are ordered
    // so the priority ones (pool, views, parking, and "Setting" — which holds the
    // "Close To …" items) come first, then the rest largest-first; the flattened
    // `facilities` follows the same order so the inline preview surfaces them too.
    const featureCategories = [];
    if (Array.isArray(item.Features)) {
        item.Features.forEach((cat) => {
            if (!cat || !Array.isArray(cat.Items) || !cat.Items.length) return;
            featureCategories.push({
                category: cat.Category || 'Features',
                // CategoryKey is the untranslated identifier; older feeds omit it, so
                // fall back to the display name (only correct for English payloads).
                categoryKey: cat.CategoryKey || cat.Category || '',
                items: cat.Items,
            });
        });
    }
    // Match the real Resales-Online category names (case-insensitive). "Setting"
    // is the category that contains the "Close To …" items the request asked for.
    // Ranking uses CategoryKey so the order holds in every language — the display
    // name is translated ("Pool" → "Piscina") and would not match these.
    const CATEGORY_PRIORITY = ['pool', 'views', 'parking', 'setting', 'features', 'kitchen', 'garden', 'climate', 'security'];
    const catRank = (name) => {
        const n = String(name || '').toLowerCase();
        const i = CATEGORY_PRIORITY.findIndex((p) => n.includes(p));
        return i === -1 ? CATEGORY_PRIORITY.length : i;
    };
    featureCategories.sort((a, b) => {
        const ra = catRank(a.categoryKey);
        const rb = catRank(b.categoryKey);
        if (ra !== rb) return ra - rb;            // priority categories first
        return b.items.length - a.items.length;   // then largest-first (as before)
    });
    const facilities = featureCategories.reduce((all, c) => all.concat(c.items), []);

    const feeYear = (v) => (num(v) > 0 ? `${money(v, currency)} /Year` : 'Not Available');
    const community = num(item.CommunityFees) > 0
        ? `${money(item.CommunityFees, currency)} /Month`
        : 'Not Available';

    const yearRaw = item.RsCompletionYear;
    const validYear = yearRaw && !['-- Select --', 'Unknown', ''].includes(yearRaw);

    const energyOk = parseInt(item.RsEnergyRatingStatus, 10) === 1;
    const energyRating = energyOk ? (item.RsEnergyRatingLetter || '—') : 'Pending';

    // ---- right-column "Property Details" + energy card (Figma 192:97) --------
    // The visible rows depend on the property type and whether it's a development;
    // the component reads these raw values and localizes the labels/values.
    const details = {
        floor: item.RsFloors,        // which floor an apartment is on (RsFloors)
        storeys: item.RsStoreys,     // building storeys (RsStoreys)
        firstOccupationLicence: item.RsFirstOccupationLicence,
        shortTermLicence: parseInt(item.RsShortTermLicence, 10) || 0, // tourist licence: 0 Unknown / 1 Yes / 2 No
    };
    // New-development-only rows. The feed sends these as strings ("0"/"1"); the
    // view layer (PropertyDetails) maps them to Yes/No/Not Known.
    const development = item.IsDevelopment ? {
        mortgageAvailable: item.DevIsMortgageAvailable,
        guaranteedRental: item.DevIsGuaranteedRental,
        soldBeforeCompletion: item.DevIsSoldBeforeCompletion,
        keyReady: item.RsKeyReady, // "0"/"1"; only shown for developments
        // Payment schedule (developments): reservation is a currency amount,
        // contract/completion are percentages. 0 → treated as "not set".
        reservation: num(item.DevReservationAmount) > 0 ? money(item.DevReservationAmount, currency) : '',
        contractPct: item.DevContractPercentage,
        constructionPct: item.DevConstructionPercentage,
        completionPct: item.DevCompletionPercentage,
    } : null;
    const energy = {
        pending: !energyOk,
        ratingLetter: energyOk ? (item.RsEnergyRatingLetter || '') : '',
        co2Letter: item.RsCO2RatingLetter || '',
        co2: num(item.RsCO2Rating),
        consumptionLetter: item.RsEnergyRatingLetter || '',
        consumption: num(item.RsEnergyRating),
        // Server-rendered certificate for the property's actual values. The
        // bundled SVGs only cover A–E, so F/G ratings fall back to the
        // "not rated" chart; this image is correct for every letter and is
        // preferred whenever the feed supplies it (see EnergyCard).
        certUrl: typeof item.EnergyRatingMedia === 'string' ? secureUrl(item.EnergyRatingMedia) : '',
    };

    // Gallery uses the larger "/w1200/" variant so the full-width photo isn't
    // upscaling a thumbnail (smaller sizes are derived per context via cdnSize —
    // e.g. w800 on mobile, thumbnails for the offer strip).
    const photos = Array.isArray(item.Photos) && item.Photos.length
        ? item.Photos.map((url) => cdnSize(secureUrl(url), 'w1200'))
        : [FALLBACK_IMG];

    // Combined subtype string, matching how the v1 gateway builds it in propq-api
    // (PropertySearchService: SubType = primary [ " - " secondary ]). The secondary
    // subtype is only appended for Commercial listings. This unifies both feeds:
    //   v1 pre-combines SubType and never sends SubType2, so the concat is a no-op;
    //   v3 sends the parts separately (SubType, SubType2) and this rebuilds the same
    //   string. Both SubType and SubType2 arrive already localized from the backend,
    //   and the " - " separator is language-agnostic (as in v1), so this needs no
    //   per-language handling.
    const propType = item.RsType || item.Type || '';
    const secondarySubType = propType === 'Commercial' ? (item.SubType2 || '') : '';
    const combinedSubType = (item.SubType || '') + (secondarySubType ? ` - ${secondarySubType}` : '');

    // Unit offers show the unit's real type/subtype instead of "New Development";
    // `isDevelopment` itself stays true so purchase costs remain new-build.
    const subType = (item.IsDevelopment && !isUnitOffer) ? 'New Development' : (combinedSubType || 'Property');
    // English fallback title; the UI builds the localized title from `titleParts`
    // via i18n.propertyTitle() (replicating production's l.formatString(l.propTitle, …)).
    const title = `${subType} ${searchTypeLabel(item.SearchType)} in ${item.RsLocation || ''}`.trim();
    const titleParts = {
        isDevelopment: isDevelopment && !isUnitOffer,
        subType: combinedSubType || 'Property',
        searchType: item.SearchType, // 'ForSale' | 'LongTermRental' | 'ShortTermRental'
        location: item.RsLocation || '',
    };
    const location = [item.RsLocation, item.RsArea, item.RsProvince].filter(Boolean).join(', ');

    const isShortTerm = item.SearchType === 'ShortTermRental';
    const isRental = isShortTerm || item.SearchType === 'LongTermRental';

    // Reference: the agency's own ref when this listing belongs to the viewing
    // agency. Ownership comes from the feed's OwnProperty flag — it's authoritative
    // (RsAgencyContactSecureId is often null even for owned listings); the
    // secure-id match is kept only as a fallback. For rentals the rental ref is
    // preferred — short-term uses RsAgencyRentalShortRef, long-term uses
    // RsAgencyRentalRef — falling back to RsAgencyRef, then the Resales id prefixed
    // with "R", so every property still shows a reference.
    const agencyOwns = item.OwnProperty === true || item.OwnProperty === 'true' || String(item.OwnProperty) === '1'
        || (agency && agency.contactSecureId != null
            && String(agency.contactSecureId) === String(item.RsAgencyContactSecureId));
    let agencyRef = item.RsAgencyRef;
    if (agencyOwns) {
        if (isShortTerm && item.RsAgencyRentalShortRef) agencyRef = item.RsAgencyRentalShortRef;
        else if (isRental && item.RsAgencyRentalRef) agencyRef = item.RsAgencyRentalRef;
    }
    let reference = (agencyOwns && agencyRef) ? agencyRef : `R${item.RsId}`;
    if (isUnitOffer) reference = `U${item.unitId}`;

    // Image banner for in-progress sale statuses. The feed's RsStatus ("Sale
    // Agreed", "Under Offer") normalises to the locale key in strings.rsStatus
    // (SaleAgreed / UnderOffer); other statuses show no banner (null).
    const statusKey = String(item.RsStatus || '').replace(/[^A-Za-z]/g, '');
    const statusBanner = (statusKey === 'SaleAgreed' || statusKey === 'UnderOffer') ? statusKey : null;

    // Rental-only: security deposit (currency; blank when absent/0).
    const securityDeposit = num(item.RsSecurityDeposit) > 0 ? money(num(item.RsSecurityDeposit), currency) : '';

    // Unit breakdown (developments, when ?unitdetails=1 returns them). Two feed
    // shapes are supported:
    //   current — UnitIds is an object: { UntTotalUnits, availableUnits: [ … ] },
    //             already filtered server-side to available units only;
    //   legacy  — UnitIds is an array of unit objects (unfiltered), or of bare ids
    //             (no detail requested), in which case there is nothing to list.
    // UntStatus: 1 available / 2 on hold / 4 sold / 8 reserved.
    const unitFeed = isUnitOffer ? null : item.UnitIds;
    const unitList = Array.isArray(unitFeed)
        ? unitFeed
        : ((unitFeed && Array.isArray(unitFeed.availableUnits)) ? unitFeed.availableUnits : []);

    const units = unitList
        .filter((u) => u && typeof u === 'object')
        .map((u) => ({
            id: u.UntId,
            name: u.UntName || '',
            // UntSubType is already the full localized typology label from the
            // backend (e.g. "Middle Floor Apartment" / "Apartamento Planta Media");
            // UntType is the English fallback.
            typology: u.UntSubType || u.UntType || '',
            floors: parseInt(u.UntNumFloors, 10) || 0,
            keyReady: u.UntKeyReady, // "0"/"1" (per-unit, mirrors dev RsKeyReady)
            beds: parseInt(u.UntNumBeds, 10) || 0,
            baths: parseFloat(u.UntNumBaths) || 0,
            area: num(u.UntInternalArea), // m² (internal / built area)
            // Terrace / garden totals, summed per unit across its outdoor spaces.
            // The matching counts say how many spaces make up each total.
            terraceArea: num(u.UntTotalTerraceSize),
            terraces: parseInt(u.UntTerraces, 10) || 0,
            gardenArea: num(u.UntTotalGardenSize),
            gardens: parseInt(u.UntGardens, 10) || 0,
            priceLabel: money(u.UntPrice, currency) || '—',
            status: parseInt(u.UntStatus, 10) || 0,
        }));

    // Total units in the development, for the "available / total" heading. The
    // current feed supplies it; legacy array feeds can only report what they sent.
    const totalUnits = (unitFeed && !Array.isArray(unitFeed) && unitFeed.UntTotalUnits != null)
        ? (parseInt(unitFeed.UntTotalUnits, 10) || 0)
        : units.length;

    // Estimated buyer costs (ESTIMATE ONLY — pending legal review). The applicable
    // tax depends on the transaction:
    //   - resale dwelling     → regional ITP (7% Andalucía / 9% Valencia, 11% over
    //                           €1m / …) — see itpRate
    //   - new build (dev)     → IVA 10% (+ AJD)
    //   - plot / land         → IVA 21% (+ AJD): plots are typically sold by a
    //                           business. A private-seller plot would pay ITP, but
    //                           seller status isn't in the feed, so we assume the
    //                           common business-sale case (flagged in the strategy doc).
    // The "low – high" range = the applicable tax + a fixed-cost buffer (notary,
    // registry, legal, plus AJD for IVA). Rates + buffers are legally sensitive and
    // MUST be maintained by legal.
    const priceNum = isUnavailable ? 0 : num(item.Price);
    const isPlot = /\b(plot|land|terreno|parcela|solar)\b/i.test(`${propType} ${combinedSubType}`);
    const taxCommunity = provinceToCommunity(item.RsProvince);
    let scenario = 'resale';
    let taxName = 'ITP';
    let taxPct = itpRate(taxCommunity, priceNum, currency);
    let ajdPct = null; // ITP resales pay no AJD
    if (isPlot) {
        scenario = 'plot'; taxName = 'IVA'; taxPct = 21; ajdPct = AJD_BY_COMMUNITY[taxCommunity];
    } else if (isDevelopment) {
        scenario = 'newbuild'; taxName = 'IVA'; taxPct = 10; ajdPct = AJD_BY_COMMUNITY[taxCommunity];
    }
    // Fixed extras on top of the headline tax (buffer, %): IVA scenarios also carry AJD.
    const fixedLow = scenario === 'resale' ? 2 : 3;
    const fixedHigh = scenario === 'resale' ? 4 : 5;
    const lowPct = taxPct + fixedLow;
    const highPct = taxPct + fixedHigh;
    const purchaseCosts = priceNum > 0 ? {
        scenario, taxName, taxPct, ajdPct, community: taxCommunity,
        lowPct, highPct,
        lowLabel: abbrCurrency(Math.round(priceNum * lowPct / 100), currency),
        highLabel: abbrCurrency(Math.round(priceNum * highPct / 100), currency),
    } : null;

    // Unit offers have no upper bounds: their stats and counts are exact values.
    const toOf = (v) => (isUnitOffer ? 0 : v);
    let priceLabel = priceRange(item, currency);
    if (isUnitOffer) priceLabel = isUnavailable ? '' : (money(item.Price, currency) || '—');

    // Treat offerId as opaque: the companion API uses "U9001" for a unit and
    // "501" for a property; RV3 also supplies composite unit IDs. Legacy feeds
    // without it keep the RsId-based id.
    let offerKey = index;
    if (item.offerId != null) offerKey = item.offerId;
    else if (item.RsId != null) offerKey = item.RsId;

    return {
        id: `rs-${offerKey}`,
        rsId: item.RsId,
        offerId: item.offerId != null ? String(item.offerId) : null,
        unitId: isUnitOffer ? item.unitId : null,
        availability: isUnitOffer ? (item.availability || 'available') : null,
        isUnitOffer,
        isUnavailable,
        reference,
        // Numeric price (feed order is preserved elsewhere) — used only to sort
        // the offers when the agency picks a price order; 0 when absent so
        // price-less offers sink to the end of an ascending sort.
        priceValue: priceNum,
        statusBanner, // 'SaleAgreed' | 'UnderOffer' | null — corner ribbon on images
        label: `Offer ${index + 1}`,
        isDevelopment,
        searchType: item.SearchType,     // 'ForSale' | 'LongTermRental' | 'ShortTermRental'
        isRental,
        isShortTerm,
        securityDeposit,
        propertyType: item.RsType || item.Type || '', // 'Apartment' | 'House' | 'Plot' | … (rv3 feed uses `Type`)
        details,
        development,
        units,
        totalUnits,
        energy,
        priceLabel,
        title,
        titleParts,
        location,
        beds,
        bedsTo: toOf(bedsTo > beds ? bedsTo : 0),
        baths,
        bathsTo: toOf(bathsTo > baths ? bathsTo : 0),
        description: stripHtml(item.Description),
        gallery: photos,
        photoCount: photos.length,
        unit,
        stats: {
            intFloor: range(intFloor, toOf(item.UsefulAreaTo)),
            build: range(item.BuiltArea, toOf(item.BuiltAreaTo)),
            terrace: range(item.TerraceArea, toOf(item.TerraceAreaTo)),
            garden: range(item.GardenPlotArea, toOf(item.GardenPlotAreaTo)),
        },
        buildYear: validYear ? yearRaw : 'Not Available',
        fees: {
            community,
            basura: feeYear(item.BasuraTax),
            ibi: feeYear(item.LocalFees),
        },
        energyRating,
        // Empty when the feed lists no features; the view substitutes a localized
        // "information on request" fallback at render (t.infoOnRequest).
        facilities,
        featureCategories,
        purchaseCosts,
        // API-provided Google-maps embed URL — same field the production location
        // component reads (AccordionLocation.js: `currentProperty.mapUrl`).
        // `MapUrl` kept as a case-tolerant fallback. When the feed has no link,
        // LocationMap falls back to a geocoded embed built from `location`.
        mapUrl: item.mapUrl || item.MapUrl || null,
        disclaimer: '',
        agency,
    };
}
